import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Chip,
  Paper
} from '@mui/material';
import { 
  Schedule as ScheduleIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../Payment/StripePaymentForm';
import { createAuthorizationPayment, confirmAuthorizationPayment } from '../../services/paymentService';
import { getProfessionalAvailability } from '../../services/invitationService';
import { useCurrency } from '../../contexts/CurrencyContext';
import { getStripePromise } from '../../utils/stripeLoader';

// ============================================================================
// SESSION DURATION CONFIGURATION
// ============================================================================
// Standard session duration in minutes - all sessions are this length
// Future: Can be extended to support professional-specific durations
const STANDARD_SESSION_DURATION = 30; // minutes

// Future configuration (uncomment when ready to support multiple durations):
// const AVAILABLE_DURATIONS = professional?.availableDurations || [30]; // Array of options
// ============================================================================

const EnhancedInvitationModal = ({ professional, open, onClose, onSuccess }) => {
  // 步骤状态
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);

  // 表单数据
  const [formData, setFormData] = useState({
    topic: '',
    message: '',
    selectedTimeSlot: null,
    duration: STANDARD_SESSION_DURATION
  });

  // 可用性选择状态
  const [selectedDate, setSelectedDate] = useState(null);

  // 周视图状态
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    return startOfWeek;
  });
  
  // 可用时间槽
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // 专业人员费率
  const [professionalRate, setProfessionalRate] = useState(0);

  // 付款相关
  const [paymentData, setPaymentData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeLoadError, setStripeLoadError] = useState(null);

  // Currency context for display
  const { convertFromGBP, getCurrencySymbol } = useCurrency();

  const steps = ['Select Availability', 'Session Details', 'Payment'];
  
  // Load professional's available time slots
  const loadAvailableSlots = useCallback(async () => {
    if (!professional?._id) return;
    
    console.log('\n🎯 === FRONTEND: LOADING AVAILABLE SLOTS ===');
    console.log('🔍 Professional Info:', {
      professionalId: professional._id,
      professionalName: `${professional.firstName} ${professional.lastName}`,
      timestamp: new Date().toISOString()
    });
    
    setLoadingSlots(true);
    try {
      const response = await getProfessionalAvailability(professional._id, { days: 30 });
      console.log('📡 Backend Response:', {
        success: response.success,
        hasData: !!response.data,
        slotsCount: response.data?.availableSlots?.length || 0,
        source: response.data?.source || 'unknown',
        rate: response.data?.rate,
        professionalId: response.data?.professionalId,
        rawResponse: response
      });

      // Store the professional's rate
      if (response.data?.rate) {
        setProfessionalRate(response.data.rate);
        console.log('💰 Professional rate set to:', response.data.rate);
      }
      
      if (response.success && response.data && response.data.availableSlots) {
        // Convert backend data format to frontend TimeSlotPicker expected format
        console.log('🔄 Converting backend slots to frontend format...');
        console.log('📊 Raw backend slots sample:', response.data.availableSlots.slice(0, 3));
        
        const formattedSlots = response.data.availableSlots.map((slot, index) => {
          const datetime = new Date(slot.datetime);
          const endTime = new Date(datetime.getTime() + slot.duration * 60000);
          
          const formatted = {
            id: `slot-${index}`,
            day: datetime.toLocaleDateString('en-US', { weekday: 'long' }),
            date: datetime.toISOString().split('T')[0],
            startTime: datetime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
            endTime: endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
            isAvailable: slot.available || true,
            datetime: slot.datetime // Keep original datetime for backend use
          };
          
          if (index < 3) {
            console.log(`🎯 Slot ${index + 1} conversion:`, {
              original: slot,
              formatted: formatted
            });
          }
          
          return formatted;
        });
        
        console.log('✅ Frontend formatted slots:', {
          totalSlots: formattedSlots.length,
          sampleSlots: formattedSlots.slice(0, 3),
          distinctDays: [...new Set(formattedSlots.map(s => s.day))],
          distinctDates: [...new Set(formattedSlots.map(s => s.date))]
        });
        
        setAvailableSlots(formattedSlots);
      } else {
        console.log('❌ Response format issue:', {
          success: response.success,
          hasData: !!response.data,
          hasSlots: !!response.data?.availableSlots,
          response: response
        });
        setError('Unable to load available time slots');
      }
    } catch (error) {
      console.error('❌ Frontend Error loading available slots:', {
        error: error.message,
        stack: error.stack,
        professionalId: professional._id
      });
      setError('Error loading available time slots');
    } finally {
      setLoadingSlots(false);
      console.log('==============================================\n');
    }
  }, [professional]);
  
  // 当模态框打开时加载时间槽
  useEffect(() => {
    if (open && professional) {
      loadAvailableSlots();
    }
  }, [open, professional, loadAvailableSlots]);

  // Load Stripe when reaching payment step
  useEffect(() => {
    const loadStripe = async () => {
      if (activeStep === 2 && !stripePromise) {
        try {
          setStripeLoadError(null);
          console.log('Loading Stripe for payment step...');
          const promise = await getStripePromise();
          setStripePromise(promise);
          console.log('Stripe loaded successfully');
        } catch (error) {
          console.error('Failed to load Stripe:', error);
          setStripeLoadError(error.message);
          setError(`Failed to load payment system: ${error.message}`);
        }
      }
    };

    loadStripe();
  }, [activeStep, stripePromise]);

  // Reset form state
  const resetForm = () => {
    setFormData({
      topic: '',
      message: '',
      selectedTimeSlot: null,
      duration: STANDARD_SESSION_DURATION
    });
    setActiveStep(0);
    setError(null);
    setPaymentData(null);
    setStripePromise(null);
    setStripeLoadError(null);
    setAvailableSlots([]);
    setSelectedDate(null);
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek);
    setCurrentWeekStart(startOfWeek);
  };
  
  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('🔍 FORM INPUT CHANGE:', { name, value, valueLength: value?.length, valueTruthy: !!value });
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };
      console.log('🔍 UPDATED FORM DATA:', { topic: newFormData.topic, message: newFormData.message });
      return newFormData;
    });
  };
  
  const handleTimeSlotSelect = (slotId) => {
    const selectedSlot = availableSlots.find(slot => slot.id === slotId);
    setFormData(prev => ({
      ...prev,
      selectedTimeSlot: selectedSlot
    }));
  };
  
  
  // Week navigation functions
  const handlePreviousWeek = () => {
    setCurrentWeekStart(prev => {
      const newStart = new Date(prev);
      newStart.setDate(prev.getDate() - 7);
      return newStart;
    });
  };
  
  const handleNextWeek = () => {
    setCurrentWeekStart(prev => {
      const newStart = new Date(prev);
      newStart.setDate(prev.getDate() + 7);
      return newStart;
    });
  };
  
  // Get week dates
  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };
  
  // Filter slots by selected date
  const getSlotsByDate = (date) => {
    if (!availableSlots.length || !date) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    return availableSlots.filter(slot => slot.date === dateStr);
  };
  
  // Group slots by time of day
  const groupSlotsByTimeOfDay = (slots) => {
    const groups = {
      morning: { label: '🌅 Morning', slots: [] },
      afternoon: { label: '☀️ Afternoon', slots: [] },
      evening: { label: '🌆 Evening', slots: [] }
    };
    
    slots.forEach(slot => {
      const hour = parseInt(slot.startTime.split(':')[0]);
      if (hour < 12) {
        groups.morning.slots.push(slot);
      } else if (hour < 17) {
        groups.afternoon.slots.push(slot);
      } else {
        groups.evening.slots.push(slot);
      }
    });
    
    return groups;
  };
  
  // Date selection handler
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };
  
  const validateStep = (step) => {
    switch (step) {
      case 0:
        return formData.selectedTimeSlot !== null;
      case 1:
        // Only validate form fields are filled
        return formData.topic.trim() !== '' && formData.message.trim() !== '';
      case 2:
        // Payment step - payment should be created and ready
        return paymentData !== null;
      default:
        return true;
    }
  };
  
  const handleNext = async () => {
    console.log('🚨 handleNext called - activeStep:', activeStep);
    console.log('🚨 validateStep result:', validateStep(activeStep));
    console.log('🚨 Current formData:', JSON.stringify(formData, null, 2));
    
    if (!validateStep(activeStep)) {
      console.log('🚨 Validation failed for step', activeStep);
      setError('Please fill in all required information');
      return;
    }
    
    setError(null);
    
    if (activeStep === 1) {
      console.log('🚨 Step 1 -> Step 2: calling createPaymentAuthorization');
      // Create payment authorization when moving from session details to payment
      await createPaymentAuthorization();
    } else {
      console.log('🚨 Regular step advance from', activeStep, 'to', activeStep + 1);
      setActiveStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };
  
  const createPaymentAuthorization = async () => {
    console.log('🚨🚨🚨 createPaymentAuthorization FUNCTION CALLED 🚨🚨🚨');
    console.log('Current formData when creating payment:', JSON.stringify(formData, null, 2));
    setPaymentLoading(true);
    try {
      console.log('🔍 FRONTEND DEBUG - Creating payment request:');
      console.log('formData.selectedTimeSlot:', formData.selectedTimeSlot);
      console.log('formData.selectedTimeSlot?.datetime:', formData.selectedTimeSlot?.datetime);
      console.log('🔍 FORM DATA VALUES:');
      console.log('formData.topic (raw):', JSON.stringify(formData.topic));
      console.log('formData.message (raw):', JSON.stringify(formData.message));
      console.log('formData.topic length:', formData.topic?.length);
      console.log('formData.message length:', formData.message?.length);
      console.log('formData.topic truthy:', !!formData.topic);
      console.log('formData.message truthy:', !!formData.message);
      
      const paymentRequest = {
        professionalId: professional._id,
        amount: calculateAmount(),
        currency: 'gbp',
        description: `Coffee Chat: ${formData.topic}`,
        sessionDetails: {
          topic: formData.topic || 'Coffee Chat Discussion',
          message: formData.message || 'Looking forward to our meeting!',
          proposedDate: formData.selectedTimeSlot?.datetime, // ✅ FIX: Use proposedDate instead of selectedTimeSlot
          selectedTimeSlot: formData.selectedTimeSlot, // Keep selectedTimeSlot for backend processing
          duration: STANDARD_SESSION_DURATION
        }
      };
      
      console.log('🔍 FRONTEND DEBUG - Payment request sessionDetails:');
      console.log('proposedDate:', paymentRequest.sessionDetails.proposedDate);
      console.log('selectedTimeSlot.datetime:', paymentRequest.sessionDetails.selectedTimeSlot?.datetime);
      console.log('🔍 FINAL SESSION DETAILS BEING SENT:');
      console.log('topic:', JSON.stringify(paymentRequest.sessionDetails.topic));
      console.log('message:', JSON.stringify(paymentRequest.sessionDetails.message));
      console.log('Full sessionDetails:', JSON.stringify(paymentRequest.sessionDetails, null, 2));
      console.log('🔍 COMPLETE PAYMENT REQUEST:', JSON.stringify(paymentRequest, null, 2));
      
      const response = await createAuthorizationPayment(paymentRequest);
      console.log('Payment authorization response:', response);
      
      if (response.success) {
        setPaymentData(response.data);
        setActiveStep(prev => prev + 1);
      } else {
        setError('Failed to create payment authorization');
      }
    } catch (error) {
      console.error('Error creating payment authorization:', error);
      const errorMessage = error.details?.message ||
                          error.message ||
                          'Error creating payment authorization. Please try again.';
      setError(errorMessage);

      // Log additional details for debugging
      if (error.statusCode) {
        console.error('Status code:', error.statusCode);
      }
      if (error.details) {
        console.error('Error details:', error.details);
      }
    } finally {
      setPaymentLoading(false);
    }
  };
  
  const handlePaymentSuccess = async (paymentIntent) => {
    console.log('🚀 handlePaymentSuccess called with:', paymentIntent);
    setPaymentLoading(true);
    try {
      if (paymentIntent.status === 'requires_capture') {
        console.log('💳 Payment status is requires_capture, confirming with backend...');
        console.log('Using paymentIntentId:', paymentData.paymentIntentId);
        
        // Payment authorized successfully, now confirm with backend
        const confirmResponse = await confirmAuthorizationPayment({
          paymentIntentId: paymentData.paymentIntentId
        });
        
        console.log('🔥 Backend confirmation response:', confirmResponse);
        
        if (confirmResponse.success) {
          console.log('✅ Payment confirmed successfully with backend');
          // Payment successful, call success callback
          if (onSuccess) {
            onSuccess({
              ...confirmResponse.data,
              sessionDetails: formData
            });
          }
          
          // Close modal
          onClose();
        } else {
          console.error('❌ Backend confirmation failed:', confirmResponse);
          setError('Backend confirmation failed');
        }
      } else {
        console.error(`❌ Wrong payment status: ${paymentIntent.status}. Expected: requires_capture`);
        setError(`Unexpected payment status: ${paymentIntent.status}`);
      }
    } catch (error) {
      console.error('❌ Error confirming payment with backend:', error);
      setError('Payment confirmation error: ' + error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(`Payment failed: ${error.message}`);
  };
  
  const calculateAmount = () => {
    // Calculate based on professional's rate and session duration
    // Note: professionalRate is now the rate for a 30-minute session
    const durationInMinutes = STANDARD_SESSION_DURATION;
    const sessionRate = professionalRate || 0;

    // Since all sessions are 30 minutes and rate is for 30-min sessions,
    // we can use the rate directly (duration should be 30)
    const totalInCurrency = sessionRate * (durationInMinutes / 30);

    // Round to nearest whole currency unit (pound, dollar, euro)
    const roundedCurrency = Math.round(totalInCurrency);

    // Convert to pence for Stripe (which requires smallest currency unit)
    const totalAmount = roundedCurrency * 100;

    console.log('💰 Amount calculation:', {
      duration: durationInMinutes,
      sessionRate: sessionRate,
      totalInCurrency: totalInCurrency.toFixed(2),
      roundedCurrency: roundedCurrency,
      totalPence: totalAmount,
      totalGBP: (totalAmount / 100).toFixed(2)
    });

    return totalAmount;
  };
  
  const formatAmount = (amount) => {
    // Convert amount in pence to GBP, then to selected currency
    const amountInGBP = amount / 100;
    const convertedAmount = convertFromGBP(amountInGBP);
    return `${getCurrencySymbol()}${convertedAmount.toFixed(2)}`;
  };
  
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        const weekDates = getWeekDates();
        const selectedDateSlots = selectedDate ? getSlotsByDate(selectedDate) : [];
        const groupedSlots = groupSlotsByTimeOfDay(selectedDateSlots);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Info Banner - All sessions are 30 minutes */}
            <Alert severity="info" sx={{ mb: 1 }}>
              All coffee chat sessions are <strong>{STANDARD_SESSION_DURATION} minutes</strong>
            </Alert>

            {/* Week View Calendar */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Select Date
                </Typography>
                <Box>
                  <IconButton onClick={handlePreviousWeek} size="small">
                    <ChevronLeft />
                  </IconButton>
                  <IconButton onClick={handleNextWeek} size="small">
                    <ChevronRight />
                  </IconButton>
                </Box>
              </Box>
              
              <Grid container spacing={1}>
                {weekDates.map((date, index) => {
                  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                  const hasSlots = getSlotsByDate(date).length > 0;
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isPast = date < new Date().setHours(0, 0, 0, 0);
                  
                  return (
                    <Grid item xs key={index} sx={{ flex: 1 }}>
                      <Paper
                        elevation={0}
                        onClick={() => !isPast && hasSlots && handleDateSelect(date)}
                        sx={{
                          p: 1,
                          textAlign: 'center',
                          cursor: (!isPast && hasSlots) ? 'pointer' : 'not-allowed',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          backgroundColor: isSelected 
                            ? 'primary.light' 
                            : isPast || !hasSlots 
                              ? 'grey.100' 
                              : 'background.paper',
                          opacity: isPast || !hasSlots ? 0.5 : 1,
                          '&:hover': {
                            backgroundColor: (!isPast && hasSlots) 
                              ? isSelected 
                                ? 'primary.light' 
                                : 'grey.50'
                              : undefined
                          }
                        }}
                      >
                        <Typography variant="caption" display="block">
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: isToday ? 'bold' : 'normal',
                            color: isSelected ? 'primary.contrastText' : 'text.primary'
                          }}
                        >
                          {date.getDate()}
                        </Typography>
                        {hasSlots && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={getSlotsByDate(date).length}
                              sx={{ 
                                height: 16, 
                                fontSize: '0.7rem',
                                backgroundColor: isSelected ? 'primary.contrastText' : 'primary.light',
                                color: isSelected ? 'primary.main' : 'primary.contrastText'
                              }} 
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Time Selection */}
            {selectedDate && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Available Times
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatAmount(calculateAmount())}
                  </Typography>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Times shown in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </Typography>

                {loadingSlots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedDateSlots.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {Object.entries(groupedSlots).map(([timeOfDay, group]) => 
                      group.slots.length > 0 && (
                        <Box key={timeOfDay}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                            {group.label}
                          </Typography>
                          <Grid container spacing={1}>
                            {group.slots.map((slot) => (
                              <Grid item xs={6} sm={4} md={3} key={slot.id}>
                                <Button
                                  variant={formData.selectedTimeSlot?.id === slot.id ? "contained" : "outlined"}
                                  onClick={() => handleTimeSlotSelect(slot.id)}
                                  sx={{ 
                                    width: '100%',
                                    py: 1,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {slot.startTime}
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )
                    )}
                  </Box>
                ) : (
                  <Alert severity="info">
                    No available time slots for this date.
                  </Alert>
                )}
              </Box>
            )}

            {!selectedDate && availableSlots.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select a date to view available times.
              </Alert>
            )}

            {availableSlots.length === 0 && !loadingSlots && (
              <Alert severity="info">
                This professional has no available time slots at the moment. Please try again later.
              </Alert>
            )}
          </Box>
        );
        
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Session Details Form */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Session Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Session Topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder="e.g., Software Engineering Career Advice"
                />
                
                <TextField
                  label="Personal Introduction and Questions"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={2}
                  required
                  placeholder="Briefly introduce yourself and what you'd like to discuss"
                />
              </Box>
            </Box>

            {/* Booking Summary */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Booking Summary
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Professional: {professional?.firstName} {professional?.lastName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Duration: {STANDARD_SESSION_DURATION} minutes
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Date: {selectedDate?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Time: {formData.selectedTimeSlot?.startTime}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                  Total: {formatAmount(calculateAmount())}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        );
        
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Booking Summary */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Booking Summary
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Professional: {professional?.firstName} {professional?.lastName}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Topic: {formData.topic}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Duration: {STANDARD_SESSION_DURATION} minutes
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Date: {selectedDate?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Time: {formData.selectedTimeSlot?.startTime}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                  Total: {formatAmount(calculateAmount())}
                </Typography>
              </CardContent>
            </Card>
            
            {/* Payment Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment
              </Typography>

              {stripeLoadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {stripeLoadError}
                </Alert>
              )}

              {!stripePromise && !stripeLoadError && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    Loading payment system...
                  </Box>
                </Alert>
              )}

              {stripePromise && paymentData?.clientSecret && (
                <Elements stripe={stripePromise}>
                  <StripePaymentForm
                    clientSecret={paymentData.clientSecret}
                    amount={calculateAmount()}
                    currency="gbp"
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    loading={paymentLoading}
                  />
                </Elements>
              )}

              {stripePromise && !paymentData?.clientSecret && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Payment data is being prepared. Please wait...
                </Alert>
              )}
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Invite {professional?.firstName} {professional?.lastName} for Coffee Chat
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent(activeStep)}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={paymentLoading}>
          Cancel
        </Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={paymentLoading}>
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 && (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={paymentLoading || !validateStep(activeStep)}
          >
            {paymentLoading ? <CircularProgress size={20} /> : 'Next'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedInvitationModal; 